import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { SEARCH_DEBOUNCE_MS } from '~/common/constants/debounces';
import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { useThrottledValue } from '~/eds/src/hooks/useThrottledValue';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import {
  getFieldsBySectionId,
  getSectionById,
  getSectionIds,
} from '~/reducers/intakeForm';
import type { QuestionFieldState } from '~/redux/selectors';
import * as selectors from '~/redux/selectors';
import { Nullable } from '~/types/Nullable';

import { INTAKE_FORM_SECTION_ID } from '../constants';
import { useWellKnownFieldAccordionSection } from './WellKnownFields/useWellKnownFieldAccordionSection';
import { WorkflowDisplayForm } from './WorkflowDisplayForm/WorkflowDisplayForm';
import { WorkflowFormSection } from './WorkflowFormSection';

const NON_RENDERABLE_FIELD_TYPES = [
  WorkflowFieldType.DateSigned,
  WorkflowFieldType.Signature,
];

interface Props {
  isEditing: boolean;
  ticketId: string;
}

export const WorkflowForm = ({ isEditing, ticketId }: Props) => {
  const intl = useIntl();
  const [search, setSearch] = useState('');
  const throttledSearch = useThrottledValue(search, SEARCH_DEBOUNCE_MS);
  const intakeForm = useSelector(selectors.selectIntakeForm);
  const isIntakeFormEDSComponents = useFlag(FlagType.IntakeFormEDSComponents);
  const isWSSIntegrationPhase2M1M3Enabled = useFlag(
    FlagType.WSSIntegrationPhase2M1M3,
  );
  const wellKnownFieldsAccordionSection = useWellKnownFieldAccordionSection({
    ticketId,
    isEditing,
    normalizedWellKnownFields: intakeForm.wellKnownFields,
    search,
  });

  const sectionIds = intakeForm.sections ? getSectionIds(intakeForm) : [];

  const accordionItems = sectionIds
    .filter((id) => {
      if (id === 'well-known-fields' && !isWSSIntegrationPhase2M1M3Enabled) {
        return false;
      }

      return getFieldsBySectionId(intakeForm, id).some(
        (field: QuestionFieldState) =>
          field.isEnabled && !NON_RENDERABLE_FIELD_TYPES.includes(field.type),
      );
    })
    .map((id) => {
      const section = getSectionById(intakeForm, id);

      return {
        content:
          isIntakeFormEDSComponents && !isEditing ? (
            <WorkflowDisplayForm
              search={throttledSearch}
              description={section.description}
              questionIds={section.questions}
            />
          ) : (
            <WorkflowFormSection
              isEditing={isEditing}
              search={throttledSearch}
              section={section}
            />
          ),
        hash: section.name,
        headerClass: INTAKE_FORM_SECTION_ID,
        isExpanded: true,
        title: section.name,
      };
    });

  if (wellKnownFieldsAccordionSection) {
    accordionItems.unshift(wellKnownFieldsAccordionSection);
  }

  const handleSearch = (updatedSearch: Nullable<string>) =>
    setSearch(updatedSearch || '');

  return (
    <Layout direction="column" spacing={2}>
      <Layout direction="column" px={4} spacing={2}>
        <SearchInput
          placeholder={intl.formatMessage({
            id: 'WorkflowForm.searchFormPlaceholder',
            defaultMessage: 'Search Form',
            description: 'Placeholder text for the form search input',
          })}
          name="Search Form"
          value={search}
          onChange={handleSearch}
        />
        {intakeForm.description && (
          <Text preset="description">{intakeForm.description}</Text>
        )}
      </Layout>
      <Accordion isIndented items={accordionItems} />
    </Layout>
  );
};
